import React from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaPencilAlt, FaCode, FaRocket } from 'react-icons/fa';

const Process = () => {
    const steps = [
        {
            icon: <FaLightbulb />,
            title: 'Discovery',
            description: 'We dive deep to understand your vision, goals, and challenges.',
        },
        {
            icon: <FaPencilAlt />,
            title: 'Design',
            description: 'We craft intuitive, visually stunning, and user-friendly designs.',
        },
        {
            icon: <FaCode />,
            title: 'Development',
            description: 'Our engineers build your solution using best-in-class tech.',
        },
        {
            icon: <FaRocket />,
            title: 'Launch',
            description: 'We deploy, monitor, and scale to ensure long-term success.',
        },
    ];

    return (
        <section id="process" className="relative min-h-screen bg-forest overflow-hidden">
            {/* Diagonal split overlay */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-forest" />
                <div
                    className="absolute inset-0 bg-[#3D2314] transform -skew-y-6 origin-top-right"
                    style={{ top: '40%' }}
                />
            </div>

            <div className="relative z-10 container mx-auto px-4 py-20">
                <motion.h2
                    className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-20 text-accent-green"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    Our Process
                </motion.h2>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto relative">
                    {steps.map((step, index) => (
                        <motion.div
                            key={step.title}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                            className="relative bg-forest/30 backdrop-blur-md p-8 rounded-xl border border-accent-green/20 
                         hover:border-accent-green/50 transition-all group text-center shadow-lg hover:shadow-accent-green/10"
                        >
                            <motion.div
                                className="text-4xl mb-4 text-accent-green group-hover:scale-125 transform transition-transform duration-300"
                                whileHover={{ rotate: [0, 10, -10, 0], transition: { duration: 0.6 } }}
                            >
                                {step.icon}
                            </motion.div>
                            <h3 className="text-2xl font-semibold mb-3 text-cream tracking-wide">
                                {step.title}
                            </h3>
                            <p className="text-cream/80 text-base leading-relaxed">
                                {step.description}
                            </p>

                            {/* Arrow Connector (for desktop only) */}
                            {index < steps.length - 1 && (
                                <div className="hidden md:block absolute top-1/2 right-[-16px] transform translate-x-1/2 -translate-y-1/2 text-accent-green/30 text-4xl pointer-events-none">
                                    →
                                </div>
                            )}
                        </motion.div>
                    ))}
                </div>

                {/* Optional Particle Animation or Subtext */}
                <motion.p
                    className="mt-20 text-center text-cream/70 text-lg"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.6 }}
                >
                    Every project is unique — we tailor each step to ensure maximum impact.
                </motion.p>
            </div>
        </section>
    );
};

export default Process;
